import useImage from "use-image";
import headToToeArrow from "../../../static/images/headToToeArrow.png";
import { useCallback, useMemo } from "react";
import { Circle, Group, Image as KonvaImage, Text } from "react-konva";

const ARROW_HEIGHT_RATIO = 0.72;
const HEAD_CIRCLE_OFFSET_RATIO = 0.115;
const CLOCK_NUMBER_PADDING = 26;
const CLOCK_HIT_RADIUS = 16;
const CLOCK_FONT_SIZE = 18;

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const hourToAngle = (hour) => ((hour % 12) * 30 + 360) % 360;

const ArrowOverlay = ({ cx, cy, angleDeg, imgW, imgH, onAngleChange }) => {
  const [arrowImg] = useImage(headToToeArrow, "anonymous");

  const arrowH = Math.min(imgW, imgH) * ARROW_HEIGHT_RATIO;
  const headDist = arrowH / 2 - arrowH * HEAD_CIRCLE_OFFSET_RATIO;

  const maxRadius = Math.min(imgW, imgH) / 2 - CLOCK_NUMBER_PADDING;
  const clockRadius = Math.max(arrowH / 2 + CLOCK_NUMBER_PADDING, arrowH / 2);
  const boundedClockRadius = Math.min(clockRadius, maxRadius);

  const handleDragMove = useCallback(
    (e) => {
      const node = e.target;
      const stage = node.getStage();
      const pos = stage.getPointerPosition();
      if (!pos) return;

      const dx = pos.x - cx;
      const dy = pos.y - cy;
      const newRad = Math.atan2(dy, dx);
      const newDeg = ((newRad * 180) / Math.PI + 90 + 360) % 360;
      onAngleChange(newDeg);

      const snappedRad = ((newDeg - 90) * Math.PI) / 180;
      node.x(cx + headDist * Math.cos(snappedRad));
      node.y(cy + headDist * Math.sin(snappedRad));
    },
    [cx, cy, headDist, onAngleChange]
  );

  const handleClockClick = useCallback(
    (hour) => {
      onAngleChange(hourToAngle(hour));
    },
    [onAngleChange]
  );

  const clockNumbers = useMemo(
    () =>
      HOURS.map((hour) => {
        const deg = hourToAngle(hour);
        const rad = ((deg - 90) * Math.PI) / 180;
        const x = cx + boundedClockRadius * Math.cos(rad);
        const y = cy + boundedClockRadius * Math.sin(rad);
        return { hour, x, y };
      }),
    [cx, cy, boundedClockRadius]
  );

  if (!arrowImg) return null;

  const arrowW = (arrowImg.naturalWidth / arrowImg.naturalHeight) * arrowH;
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  const headHandleX = cx + headDist * Math.cos(rad);
  const headHandleY = cy + headDist * Math.sin(rad);

  return (
    <Group>
      {clockNumbers.map(({ hour, x, y }) => (
        <Group
          key={hour}
          x={x}
          y={y}
          onClick={() => handleClockClick(hour)}
          onTap={() => handleClockClick(hour)}
          data-testid={`clock-number-${hour}`}
        >
          <Circle radius={CLOCK_HIT_RADIUS} fill="#0052cc" />
          <Text
            text={String(hour)}
            fontSize={CLOCK_FONT_SIZE}
            fill="#ffffff"
            align="center"
            verticalAlign="middle"
            width={CLOCK_HIT_RADIUS * 2}
            height={CLOCK_HIT_RADIUS * 2}
            offsetX={CLOCK_HIT_RADIUS}
            offsetY={CLOCK_HIT_RADIUS}
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "pointer";
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = "default";
            }}
          />
        </Group>
      ))}

      <KonvaImage
        image={arrowImg}
        x={cx}
        y={cy}
        width={arrowW}
        height={arrowH}
        offsetX={arrowW / 2}
        offsetY={arrowH / 2}
        rotation={angleDeg}
        listening={false}
      />
      <Circle
        x={headHandleX}
        y={headHandleY}
        radius={35}
        fill="transparent"
        draggable
        onDragMove={handleDragMove}
        onDragEnd={(e) => {
          const snappedRad = ((angleDeg - 90) * Math.PI) / 180;
          e.target.x(cx + headDist * Math.cos(snappedRad));
          e.target.y(cy + headDist * Math.sin(snappedRad));
        }}
      />
    </Group>
  );
};

export default ArrowOverlay;
